import { RETURNS_SETTINGS_TAB_QUERY_PARAM } from "@redotech/redo-settings-common/flat-team-settings/returns/returns-settings-tab";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { settingsWrapperManifest } from "@redotech/redo-settings-common/settings-catalog";
import { getReturnsSettingsTabForGroup } from "@redotech/redo-settings-common/settings-catalog/returns-new-settings";

const BASE_URL = "https://redo.local";

export function getSettingsFocusedSettingHref({
  groupId,
  href,
  settingId,
}: {
  groupId: string;
  href: string;
  settingId: string;
}): string {
  const url = withoutFocusParams(href);
  onSettingReturnsTab(url, groupId, settingId);

  if (supportsSettingsHighlight(groupId, settingId)) {
    url.searchParams.set("group", groupId);
    url.searchParams.set("setting", settingId);
    url.searchParams.set("highlight", "both");
  } else if (supportsSectionHighlight(groupId)) {
    // Setting isn't individually wrapped (e.g. a shared card), but its section
    // is — glow the section rather than emit a link with no highlight.
    url.searchParams.set("group", groupId);
    url.searchParams.set("highlight", "one");
  }

  return toHref(url);
}

/** Group-level focus for links with no specific setting (e.g. an agent card
 * targeting a whole group): scrolls + glows the wrapped section, if any. */
export function getSettingsFocusedGroupHref({
  groupId,
  href,
}: {
  groupId: string;
  href: string;
}): string {
  const url = withoutFocusParams(href);

  if (supportsSectionHighlight(groupId)) {
    url.searchParams.set("group", groupId);
    url.searchParams.set("highlight", "one");
  }

  return toHref(url);
}

/** A group split across returns tabs links to its main tab; the setting may sit on another. */
function onSettingReturnsTab(url: URL, groupId: string, settingId: string) {
  if (
    !url.pathname.endsWith(`/${RETURNS_CONSOLIDATED_SETTINGS_PATH}`) ||
    !url.searchParams.has(RETURNS_SETTINGS_TAB_QUERY_PARAM)
  ) {
    return;
  }
  const tab = getReturnsSettingsTabForGroup(groupId, settingId);
  if (tab) {
    url.searchParams.set(RETURNS_SETTINGS_TAB_QUERY_PARAM, tab);
  }
}

function withoutFocusParams(href: string): URL {
  const url = new URL(href, BASE_URL);
  url.searchParams.delete("group");
  url.searchParams.delete("setting");
  url.searchParams.delete("highlight");
  url.hash = "";
  return url;
}

function toHref(url: URL): string {
  if (url.origin === BASE_URL) {
    return `${url.pathname}${url.search}`;
  }

  return url.toString();
}

/** Wrapped ⇔ highlightable: a `<SettingWrapper>` with this id exists (in the
 * requested section, or standalone) per the generated wrapper manifest. */
function supportsSettingsHighlight(
  groupId: string,
  settingId: string,
): boolean {
  return settingsWrapperManifest.settings.some(
    (entry) =>
      entry.id === settingId &&
      (entry.sectionId === undefined || entry.sectionId === groupId),
  );
}

function supportsSectionHighlight(groupId: string): boolean {
  return settingsWrapperManifest.sections.some((entry) => entry.id === groupId);
}
